import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import axios from "axios";
import { useNotification } from "../../../../context/NotificationContext";

import { APP_INVENTORY_API_BASE } from "../../../../config/api";
const API_BASE = `${APP_INVENTORY_API_BASE}`;
const CATEGORY_OPTIONS = [
  "GENERAL",
  "IOT_DEVICE",
  "SENSOR",
  "LAB_EQUIPMENT",
  "IT_ASSET",
  "FURNITURE",
  "NETWORK_EQUIPMENT",
];
const DATA_TYPE_OPTIONS = [
  "text",
  "number",
  "boolean",
  "date",
  "select",
  "multiselect",
];

function emptyField(sortOrder = 0) {
  return {
    field_key: "",
    label: "",
    data_type: "text",
    required: false,
    placeholder: "",
    help_text: "",
    default_value: "",
    options: "",
    sort_order: sortOrder,
  };
}

function emptyForm() {
  return {
    name: "",
    code: "",
    category: "GENERAL",
    description: "",
    icon_key: "",
    is_active: true,
    fields: [emptyField(0)],
  };
}

function mapAssetTypeToForm(assetType) {
  const fields =
    assetType?.fields?.length > 0
      ? assetType.fields.map((field, index) => ({
          field_key: field.field_key || "",
          label: field.label || "",
          data_type: field.data_type || "text",
          required: Boolean(field.required),
          placeholder: field.placeholder || "",
          help_text: field.help_text || "",
          default_value:
            field.default_value === null || field.default_value === undefined
              ? ""
              : String(field.default_value),
          options: Array.isArray(field.options) ? field.options.join(", ") : "",
          sort_order: field.sort_order ?? index,
        }))
      : [emptyField(0)];

  return {
    name: assetType?.name || "",
    code: assetType?.code || "",
    category: assetType?.category || "GENERAL",
    description: assetType?.description || "",
    icon_key: assetType?.icon_key || "",
    is_active: assetType?.is_active ?? true,
    fields,
  };
}

export default function AssetTypeManagerModal({
  open,
  onClose,
  onSuccess,
  token,
  activeOrg,
}) {
  const { notification, showNotification, closeNotification } =
  useNotification();
  const [assetTypes, setAssetTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [mode, setMode] = useState("create");
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  //const [successMessage, setSuccessMessage] = useState("");
  const [formErrors, setFormErrors] = useState({
  name: "",
  code: "",
});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
    }),
    [activeOrg, token],
  );

  const normalizedPayload = useMemo(
    () => ({
      ...form,
      name: form.name.trim(),
      code: form.code.trim(),
      description: form.description.trim(),
      fields: form.fields
        .filter((field) => field.field_key.trim() || field.label.trim())
        .map((field, index) => ({
          ...field,
          field_key: field.field_key.trim(),
          label: field.label.trim(),
          help_text: field.help_text.trim(),
          placeholder: field.placeholder.trim(),
          default_value: field.default_value,
          options:
            field.data_type === "select" || field.data_type === "multiselect"
              ? field.options
                  .split(",")
                  .map((item) => item.trim())
                  .filter(Boolean)
              : [],
          sort_order: index,
        })),
    }),
    [form],
  );

  const selectedAssetType = useMemo(
    () =>
      assetTypes.find(
        (assetType) => String(assetType.id) === String(selectedTypeId),
      ) || null,
    [assetTypes, selectedTypeId],
  );

  const resetForCreate = useCallback(() => {
    setMode("create");
    setSelectedTypeId(null);
    setForm(emptyForm());
    setError("");
    //setSuccessMessage("");
  }, []);

  const loadAssetTypes = useCallback(async () => {
    setLoadingTypes(true);
    try {
      const res = await axios.get(
        `${API_BASE}/asset-types?include_inactive=true`,
        {
          headers: authHeaders,
        },
      );
      const items = res.data?.data || [];
      setAssetTypes(items);

      if (items.length > 0) {
        if (mode === "edit" && selectedTypeId) {
          const refreshed = items.find(
            (assetType) => String(assetType.id) === String(selectedTypeId),
          );
          if (refreshed) {
            setForm(mapAssetTypeToForm(refreshed));
          }
        } else if (mode !== "create" && !selectedTypeId) {
          setSelectedTypeId(items[0].id);
          setMode("edit");
          setForm(mapAssetTypeToForm(items[0]));
        }
      } else if (mode !== "create") {
        resetForCreate();
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load asset types");
    } finally {
      setLoadingTypes(false);
    }
  }, [authHeaders, mode, resetForCreate, selectedTypeId]);

  useEffect(() => {
    if (!open) return;
    loadAssetTypes();
  }, [open, loadAssetTypes]);

  const handleSelectType = (assetType) => {
    setMode("edit");
    setSelectedTypeId(assetType.id);
    setForm(mapAssetTypeToForm(assetType));
    setError("");
    // setSuccessMessage("");
  };

  const updateField = (index, key, value) => {
    setForm((prev) => ({
      ...prev,
      fields: prev.fields.map((field, idx) =>
        idx === index ? { ...field, [key]: value } : field,
      ),
    }));
  };

  const handleAddField = () => {
    setForm((prev) => ({
      ...prev,
      fields: [...prev.fields, emptyField(prev.fields.length)],
    }));
  };

  const handleRemoveField = (index) => {
    setForm((prev) => ({
      ...prev,
      fields:
        prev.fields.length === 1
          ? [emptyField(0)]
          : prev.fields
              .filter((_, idx) => idx !== index)
              .map((field, idx) => ({ ...field, sort_order: idx })),
    }));
  };

  const handleSave = async () => {
  const errors = {};

  if (!form.name?.trim()) {
    errors.name = "Required";
  }

  if (!form.code?.trim()) {
    errors.code = "Required";
  }

  setFormErrors(errors);

  // 🚫 stop if validation fails
  if (Object.keys(errors).length > 0) return;

  setSaving(true);
  setError("");
  //setSuccessMessage("");
    try {
      if (mode === "edit" && selectedTypeId) {
        await axios.patch(
          `${API_BASE}/asset-types/${selectedTypeId}`,
          normalizedPayload,
          { headers: authHeaders },
        );
        showNotification("Asset type updated successfully.");
      } else {
        await axios.post(`${API_BASE}/asset-types`, normalizedPayload, {
          headers: authHeaders,
        });
        showNotification("Asset type created successfully.");
      }

      await loadAssetTypes();
      onSuccess?.();

      if (mode === "create") {
        const res = await axios.get(
          `${API_BASE}/asset-types?include_inactive=true`,
          {
            headers: authHeaders,
          },
        );
        const items = res.data?.data || [];
        setAssetTypes(items);
        const created = items.find(
          (assetType) =>
            assetType.code === normalizedPayload.code ||
            assetType.name === normalizedPayload.name,
        );
        if (created) {
          handleSelectType(created);
        }
      }
    } catch (err) {
      const details = err.response?.data?.details;
      setError(
        Array.isArray(details) && details.length
          ? details.join(", ")
          : err.response?.data?.error ||
              (mode === "edit"
                ? "Failed to update asset type"
                : "Failed to create asset type"),
      );
    } finally {
      setSaving(false);
    }
  };
const handleClose = () => {
  setFormErrors({
    name: "",
    code: "",
  });

  setForm(emptyForm()); // optional but recommended
  onClose();
};
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 0 } }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",

          px: 2.5,
          py: 2,

          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",

          boxShadow: "0 30px 80px rgba(15, 23, 42, 0.25)",
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="#fff">
            Asset Type Manager
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.7)", mt: 0.5 }}
          >
            Create, review, and update reusable inventory asset type profiles.
          </Typography>
        </Box>

        <IconButton
          onClick={handleClose}
          sx={{
            color: "#fff",
            backgroundColor: "rgba(255,255,255,0.1)",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.2)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Stack spacing={2.5}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Collapse in={notification.open}>
  <Alert
    severity={notification.severity}
    onClose={closeNotification}
  >
    {notification.message}
  </Alert>
</Collapse>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
              gap: 3,
              alignItems: "start",
            }}
          >
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                overflow: "hidden",
                bgcolor: "background.paper",
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  gap: { xs: 1.5, sm: 0 },
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box sx={{ width: "100%" }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Existing Asset Types
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {assetTypes.length} configured profiles
                  </Typography>
                </Box>

                <Button
                  size="small"
                  variant={mode === "create" ? "contained" : "outlined"}
                  startIcon={<AddIcon />}
                  onClick={resetForCreate}
                  sx={{
                    width: { xs: "100%", sm: "auto" }, // 👈 full width only mobile
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                  }}
                >
                  New Type
                </Button>
              </Box>

              {loadingTypes ? (
                <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
                  <CircularProgress size={28} />
                </Box>
              ) : assetTypes.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Inventory2OutlinedIcon
                    sx={{ fontSize: 30, color: "text.disabled", mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    No asset types created yet.
                  </Typography>
                </Box>
              ) : isMobile ? (
                // ✅ MOBILE → SELECT
                <FormControl fullWidth size="small" sx={{ p: 2 }}>
                  <InputLabel 
                  sx={{
                    
                  }}
                  >Select Asset Type</InputLabel>

                  <Select
                    value={selectedTypeId || ""}
                    label="Select Asset Type"
                    onChange={(e) => {
                      const selected = assetTypes.find(
                        (a) => String(a.id) === String(e.target.value),
                      );
                      handleSelectType(selected); // ✅ same as desktop
                    }}
                  >
                    {assetTypes.map((assetType) => {
                      const selected =
                        mode === "edit" &&
                        String(assetType.id) === String(selectedTypeId);

                      return (
                        <MenuItem key={assetType.id} value={assetType.id}>
                          <Box>
                            {/* TOP ROW */}
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              flexWrap="wrap"
                            >
                              <Typography variant="subtitle2" fontWeight={700}>
                                {assetType.name}
                              </Typography>

                              <Chip
                                size="small"
                                label={assetType.code}
                                variant="outlined"
                              />

                              {!assetType.is_active && (
                                <Chip size="small" label="Inactive" />
                              )}
                            </Stack>

                            {/* BOTTOM TEXT */}
                            <Box mt={0.5}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {assetType.category} •{" "}
                                {assetType.fields?.length || 0} fields
                              </Typography>

                              {assetType.description && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    mt: 0.5,
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                >
                                  {assetType.description}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              ) : (
                // ✅ DESKTOP → LIST
                <List sx={{ p: 0, maxHeight: 520, overflowY: "auto" }}>
                  {assetTypes.map((assetType) => {
                    const selected =
                      mode === "edit" &&
                      String(assetType.id) === String(selectedTypeId);

                    return (
                      <ListItemButton
                        key={assetType.id}
                        selected={selected}
                        onClick={() => handleSelectType(assetType)}
                        sx={{
                          alignItems: "flex-start",
                          py: 1.75,
                          px: 2,
                          borderBottom: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <ListItemText
                          primary={
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              flexWrap="wrap"
                            >
                              <Typography variant="subtitle2" fontWeight={700}>
                                {assetType.name}
                              </Typography>

                              <Chip
                                size="small"
                                label={assetType.code}
                                variant="outlined"
                              />

                              {!assetType.is_active && (
                                <Chip size="small" label="Inactive" />
                              )}
                            </Stack>
                          }
                          secondary={
                            <Box mt={0.75}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {assetType.category} •{" "}
                                {assetType.fields?.length || 0} fields
                              </Typography>

                              {assetType.description && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    mt: 0.75,
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                >
                                  {assetType.description}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </Box>

            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                bgcolor: "background.paper",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  py: 2,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {mode === "edit" ? "Edit Asset Type" : "Create Asset Type"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.5}>
                    {mode === "edit"
                      ? "Update metadata rules, labels, and required fields for this profile."
                      : "Define a new reusable asset profile and the metadata fields it should enforce."}
                  </Typography>
                </Box>
                {mode === "edit" ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <EditOutlinedIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      {selectedAssetType?.name || "Selected type"}
                    </Typography>
                  </Stack>
                ) : null}
              </Box>

              <Box sx={{ p: 2.5 }}>
                <Stack spacing={2}>
                 <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
  <TextField
    fullWidth
    required
    label="Type Name"
    value={form.name}
    error={!!formErrors.name}
    helperText={formErrors.name}
    onChange={(e) => {
      const value = e.target.value;

      setForm((prev) => ({ ...prev, name: value }));

      if (value.trim()) {
        setFormErrors((prev) => ({ ...prev, name: "" }));
      }
    }}
  />

  <TextField
    fullWidth
    required
    label="Code"
    value={form.code}
    error={!!formErrors.code}
    helperText={
      formErrors.code ||
      "Short unique code, for example LAPTOP or IOT_SENSOR."
    }
    onChange={(e) => {
      const value = e.target.value;

      setForm((prev) => ({ ...prev, code: value }));

      if (value.trim()) {
        setFormErrors((prev) => ({ ...prev, code: "" }));
      }
    }}
  />
</Stack>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      select
                      fullWidth
                      label="Category"
                      value={form.category}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          category: e.target.value,
                        }))
                      }
                    >
                      {CATEGORY_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      fullWidth
                      label="Icon Key"
                      value={form.icon_key || ""}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          icon_key: e.target.value,
                        }))
                      }
                      helperText="Optional icon key for future visual mapping."
                    />
                  </Stack>

                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Description"
                    value={form.description}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                  />

                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: "action.hover",
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "flex-start", sm: "center" },
                      justifyContent: "space-between",
                      gap: { xs: 1.5, sm: 0 },
                    }}
                  >
                    <Box sx={{ width: "100%" }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Asset Type Status
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Inactive types remain visible in history but should not
                        be used for new assets.
                      </Typography>
                    </Box>

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{
                        width: { xs: "100%", sm: "auto" }, // 👈 full width on mobile
                        justifyContent: { xs: "space-between", sm: "flex-end" }, // 👈 spread nicely on mobile
                      }}
                    >
                      <Typography variant="body2">Inactive</Typography>

                      <Switch
                        checked={form.is_active}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            is_active: e.target.checked,
                          }))
                        }
                      />

                      <Typography variant="body2">Active</Typography>
                    </Stack>
                  </Box>

                  <Divider />

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" }, // 👈 stack on mobile
                      alignItems: { xs: "flex-start", sm: "center" },
                      justifyContent: "space-between",
                      gap: { xs: 1.5, sm: 0 }, // 👈 spacing for mobile
                    }}
                  >
                    <Box sx={{ width: "100%" }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Type-Specific Fields
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Configure the metadata that assets of this type must or
                        can store.
                      </Typography>
                    </Box>

                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddField}
                      sx={{
                        width: { xs: "100%", sm: "auto" },
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      Add Field
                    </Button>
                  </Box>

                  <Stack spacing={2}>
                    {form.fields.map((field, index) => (
                      <Box
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 2.5,
                          p: 2,
                          bgcolor: "background.default",
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={2}
                        >
                          <Typography variant="subtitle2" fontWeight={700}>
                            Field {index + 1}
                          </Typography>
                          <IconButton onClick={() => handleRemoveField(index)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        <Stack spacing={2}>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                          >
                            <TextField
                              fullWidth
                              label="Field Key"
                              value={field.field_key}
                              onChange={(e) =>
                                updateField(index, "field_key", e.target.value)
                              }
                              helperText="Lowercase snake_case, for example battery_capacity."
                            />
                            <TextField
                              fullWidth
                              label="Label"
                              value={field.label}
                              onChange={(e) =>
                                updateField(index, "label", e.target.value)
                              }
                            />
                          </Stack>

                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                          >
                            <TextField
                              select
                              fullWidth
                              label="Data Type"
                              value={field.data_type}
                              onChange={(e) =>
                                updateField(index, "data_type", e.target.value)
                              }
                            >
                              {DATA_TYPE_OPTIONS.map((option) => (
                                <MenuItem key={option} value={option}>
                                  {option}
                                </MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              fullWidth
                              label="Default Value"
                              value={field.default_value}
                              onChange={(e) =>
                                updateField(
                                  index,
                                  "default_value",
                                  e.target.value,
                                )
                              }
                            />
                          </Stack>

                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                          >
                            <TextField
                              fullWidth
                              label="Placeholder"
                              value={field.placeholder}
                              onChange={(e) =>
                                updateField(
                                  index,
                                  "placeholder",
                                  e.target.value,
                                )
                              }
                            />
                            <TextField
                              fullWidth
                              label="Help Text"
                              value={field.help_text}
                              onChange={(e) =>
                                updateField(index, "help_text", e.target.value)
                              }
                            />
                          </Stack>

                          {(field.data_type === "select" ||
                            field.data_type === "multiselect") && (
                            <TextField
                              fullWidth
                              label="Options"
                              value={field.options}
                              onChange={(e) =>
                                updateField(index, "options", e.target.value)
                              }
                              helperText="Comma-separated values, for example New, In Use, Retired."
                            />
                          )}

                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", sm: "row" }, // 👈 stack on mobile
                              alignItems: { xs: "flex-start", sm: "center" },
                              justifyContent: "space-between",
                              gap: { xs: 1.5, sm: 0 }, // 👈 spacing for mobile
                            }}
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ width: "100%" }}
                            >
                              Make this field mandatory while registering an
                              asset of this type.
                            </Typography>

                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{
                                width: { xs: "100%", sm: "auto" }, // 👈 full width on mobile
                                justifyContent: {
                                  xs: "space-between",
                                  sm: "flex-end",
                                }, // 👈 better spacing on mobile
                              }}
                            >
                              <Typography variant="body2">Optional</Typography>

                              <Switch
                                checked={field.required}
                                onChange={(e) =>
                                  updateField(
                                    index,
                                    "required",
                                    e.target.checked,
                                  )
                                }
                              />

                              <Typography variant="body2">Required</Typography>
                            </Stack>
                          </Box>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Stack>
              </Box>
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} sx={{ textTransform: "none" }}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 700,
            background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
          }}
        >
          {saving
            ? mode === "edit"
              ? "Saving…"
              : "Creating…"
            : mode === "edit"
              ? "Save Changes"
              : "Create Asset Type"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
